import { forwardRef, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import styles from './languageHeader.module.scss'
import { LanguageOption } from './types'
import { ReactComponent as DropdownArrowIcon } from '../../images/icons/icn_arrow_down.svg'
import { ReactComponent as CloseIcon } from '../../images/icons/icn_close.svg'
import { useClickOutside } from 'hooks/use-click-outside'

interface LanguageHeaderProps {
  isVisible: boolean
  message: string
  updateText: string
  currentLanguage: LanguageOption
  languages: LanguageOption[]
  onClose: () => void
}

const LanguageHeader = forwardRef<HTMLDivElement, LanguageHeaderProps>((props, ref) => {
  const { t } = useTranslation(['strCommonUI'])
  const { isVisible, message, updateText, currentLanguage, languages, onClose } = props
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageOption>(currentLanguage)
  const [shouldRender, setShouldRender] = useState(true)

  const closeDropdown = useCallback(() => {
    setIsDropdownOpen(false)
  }, [])

  const dropdownRef = useClickOutside<HTMLDivElement>(closeDropdown)

  useEffect(() => {
    const savedLanguagePref = localStorage.getItem('userPreferredLanguage')
    if (savedLanguagePref) {
      setShouldRender(false)
      onClose()
    }
  }, [onClose])

  if (!shouldRender || !isVisible) return null

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen)
  }

  const handleLanguageSelect = (lang: LanguageOption) => {
    setSelectedLanguage(lang)
    setIsDropdownOpen(false)

    localStorage.setItem('userPreferredLanguage', JSON.stringify(lang))
  }

  const handleUpdateClick = () => {
    if (selectedLanguage.code !== currentLanguage.code) {
      localStorage.setItem('userPreferredLanguage', JSON.stringify(selectedLanguage))
      window.location.href = selectedLanguage.url || `/${selectedLanguage.code}`
    } else {
      localStorage.setItem('userPreferredLanguage', JSON.stringify(currentLanguage))
      onClose()
    }
  }

  const handleCloseClick = () => {
    localStorage.setItem('userPreferredLanguage', JSON.stringify(currentLanguage))
    onClose()
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      toggleDropdown()
      e.preventDefault()
    }
  }

  return (
    <div ref={ref} className={styles.headerLanguageBlock} data-testid='language-header'>
      <div className={styles.container}>
        <div className={styles.languageInner}>
          <div className={styles.languageMessage} dangerouslySetInnerHTML={{ __html: message }} />

          <div className={styles.headerLanguageControls}>
            {languages.length > 0 && (
              <div className={styles.headerLanguageSelectorWrapper} ref={dropdownRef}>
                <div
                  className={styles.headerInputBoxes}
                  onClick={toggleDropdown}
                  onKeyDown={handleKeyDown}
                  role='button'
                  tabIndex={0}
                  aria-haspopup='listbox'
                  aria-expanded={isDropdownOpen}
                  aria-label={t('selectLanguage', 'Select language')}
                >
                  <img
                    src={selectedLanguage.flagUrl}
                    alt={selectedLanguage.name}
                    className={styles.flag}
                  />
                  <span className={styles.dropdownText}>{selectedLanguage.name}</span>
                  <DropdownArrowIcon
                    className={classNames(styles.headerDropdownArrow, {
                      [styles.open]: isDropdownOpen,
                    })}
                  />
                </div>

                {isDropdownOpen && (
                  <div
                    className={styles.headerLanguageDropdown}
                    role='listbox'
                    aria-label={t('availableLanguages', 'Available languages')}
                  >
                    {languages.map(lang => (
                      <a
                        key={lang.code}
                        href={lang.url || `/${lang.code}`}
                        className={styles.languageOption}
                        onClick={e => {
                          e.preventDefault()
                          handleLanguageSelect(lang)
                        }}
                        role='option'
                        aria-selected={lang.code === selectedLanguage.code}
                      >
                        <img src={lang.flagUrl} alt={lang.name} className={styles.headerFlagIcon} />
                        {lang.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button className={styles.headerUpdateButton} onClick={handleUpdateClick}>
              {updateText}
            </button>

            <button
              className={styles.rightIconHolder}
              onClick={handleCloseClick}
              aria-label={t('button.close', 'close')}
            >
              <CloseIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
})

export default LanguageHeader
